import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { getLegacyCustomerName, TaskAttachment, ApprovedUser } from "@/types";
import { toast } from "sonner";
import { useChannelPartners } from "@/hooks/useChannelPartners";
import { useDsaPartners } from "@/hooks/useDsaPartners";
import { useBankers } from "@/hooks/useBankers";
import { isBasicUser, isRelationalManager } from "@/lib/rbac";
import { useLeads } from "@/hooks/useLeads";
import { TEST_ADMIN_USER_ID } from "@/lib/constants";
import { format } from "date-fns";
import { User, Users, Share2, ChevronDown, Calendar, Loader2, Mic, CreditCard, Building, RotateCcw } from "lucide-react";
import { useTasks } from "@/hooks/useTasks";
import { uploadTaskAttachment } from "@/services/taskService";
import { useAppConfig } from "@/hooks/useAppConfig";
import { useFileUpload } from "@/hooks/useFileUpload";
import { FileUploadSection } from "@/components/common/FileUploadSection";
import { useAuth } from "@/contexts/AuthContext";
import { getAllActiveUsers } from "@/services/userService";
import { useModal } from "@/contexts/ModalContext";
import { useSpeechRecognition } from "@/hooks/useSpeechRecognition";

interface AddTaskDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultLeadId?: string;
  hideCustomerSelection?: boolean;
}

export function AddTaskDialog({ open, onOpenChange, defaultLeadId, hideCustomerSelection = false }: AddTaskDialogProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addTask, updateTask } = useTasks();
  const { config } = useAppConfig();
  const isBasic = isBasicUser(user);
  const isRM = isRelationalManager(user);
  // Basic users and relational managers work with DSA partners (not channel partners)
  const usesDsaPartners = isBasic || isRM;
  const { partners: channelPartners = [], loading: partnersLoading } = useChannelPartners();
  const { partners: dsaPartners = [], loading: dsaPartnersLoading } = useDsaPartners({ enabled: usesDsaPartners });
  const { bankers, loading: bankersLoading, isFavorite: isBankerFavorite } = useBankers();
  const { leads, loading: leadsLoading } = useLeads();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [leadId, setLeadId] = useState<string>(defaultLeadId || '');
  const [partnerId, setPartnerId] = useState<string>('');
  const [smId, setSmId] = useState<string>('');
  const [dueDate, setDueDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [dueTime, setDueTime] = useState(format(new Date(), "HH:mm"));
  const [shareOpen, setShareOpen] = useState(false);
  const [sharedWith, setSharedWith] = useState<string[]>([]);
  const [shareWithAll, setShareWithAll] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [availableUsers, setAvailableUsers] = useState<ApprovedUser[]>([]);

  const fileUpload = useFileUpload({
    maxSizeMB: config.maxFileUploadSizeMB,
    maxFiles: config.maxFileUploads,
    allowedFileTypes: config.allowedFileTypes,
    uploadMode: 'deferred',
  });
  const { isListening, toggleVoiceInput } = useSpeechRecognition({
    onTranscript: (text) => setDescription(prev => {
      const newText = prev ? `${prev} ${text}` : text;
      return newText.substring(0, 500);
    })
  });

  // Load users from Firebase (exclude members)
  useEffect(() => {
    const loadUsers = async () => {
      try {
        const usersData = await getAllActiveUsers();
        // Filter out users with "member" role
        const filteredUsers = usersData.filter(user => 
          (!user.roles || !user.roles.includes('member')) &&
          (user.userId || user.id) !== TEST_ADMIN_USER_ID
        );
        setAvailableUsers(filteredUsers);
      } catch (error) {
        console.error('Error loading users:', error);
      }
    };
    loadUsers();
  }, []);
  


  const handleSubmit = async () => {
    // Validate description
    if (!description.trim()) {
      toast.error("Please enter what to remind");
      return;
    }

    // Validate description length (max 500 characters)
    if (description.length > 500) {
      toast.error("Description must be 500 characters or less");
      return;
    }

    // Validate date and time are provided
    if (!dueDate) {
      toast.error("Please select a due date");
      return;
    }

    if (!dueTime) {
      toast.error("Please select a due time");
      return;
    }

    // Validate due date is in the future
    const dueDateTime = new Date(`${dueDate}T${dueTime}`);
    if (isNaN(dueDateTime.getTime())) {
      toast.error("Invalid date or time format");
      return;
    }

    if (dueDateTime <= new Date()) {
      toast.error("Reminder time must be in the future");
      return;
    }

    setIsSaving(true);

    try {
      const userId = user?.id || 'unknown';

    // Get linked entity names for toast
    let linkedNames: string[] = [];
    let linkedLeadId: string | undefined = undefined;
    let linkedConnectorId: string | undefined = undefined;
    let linkedSmContactId: string | undefined = undefined;

    if (!hideCustomerSelection) {
      // Customer link
      if (leadId && leadId !== 'none') {
        const lead = leads.find(l => l.id === leadId);
        if (lead) linkedNames.push(getLegacyCustomerName(lead));
        linkedLeadId = leadId;
      }
      
      // Banker link
      if (smId && smId !== 'none') {
        const sm = bankers.find(s => s.id === smId);
        if (sm) linkedNames.push(sm.name);
        linkedSmContactId = smId;
      }
      
      // Partner link
      if (partnerId && partnerId !== 'none') {
        const partnerList = usesDsaPartners ? dsaPartners : channelPartners;
        const partner = partnerList.find(c => c.id === partnerId);
        if (partner) linkedNames.push(partner.name);
        linkedConnectorId = partnerId;
      }
    } else if (defaultLeadId) {
      linkedLeadId = defaultLeadId;
      const lead = leads.find(l => l.id === defaultLeadId);
      if (lead) linkedNames.push(getLegacyCustomerName(lead));
    }

      const dueDateTime = new Date(`${dueDate}T${dueTime}`);
      
      const newTask = {
        title: description.split('\n')[0].substring(0, 50), // Use first line of description as title
        description,
        type: 'reminder' as const,
        leadId: linkedLeadId,
        connectorId: linkedConnectorId,
        smContactId: linkedSmContactId,
        dueDate: dueDateTime,
        priority: 'medium' as const, // Always medium priority
        completed: false,
        attachments: [] as TaskAttachment[],
        sharedWith: sharedWith.length > 0 ? sharedWith : undefined,
        createdBy: user?.id || 'unknown', // useTasks hook will set this to user.id (dsaUserId)
        createdByName: user?.name || 'Unknown',
        createdAt: new Date(),
      };

      // Create task first to get the taskId, then upload files into the correct storage path
      const taskId = await addTask(newTask);

      // Upload files now that we have the taskId
      if (fileUpload.pendingFiles.length > 0) {
        toast.info(`Uploading ${fileUpload.pendingFiles.length} file${fileUpload.pendingFiles.length > 1 ? 's' : ''}...`);
        try {
          const uploadedAttachments = await fileUpload.executeUploads(
            (file) => uploadTaskAttachment(file, userId, taskId)
          );
          await updateTask(taskId, { attachments: uploadedAttachments });
        } catch (uploadError) {
          console.error('Error uploading attachments:', uploadError);
          toast.warning('Reminder saved, but some files failed to upload');
        }
      }

      const linkedMessage = linkedNames.length > 0 ? ` for ${linkedNames.join(', ')}` : '';
      const shareMessage = sharedWith.length > 0 
        ? ` (shared with ${shareWithAll ? 'all' : sharedWith.length + ' colleague' + (sharedWith.length > 1 ? 's' : '')})`
        : '';
      toast.success(`Reminder added${linkedMessage}${shareMessage}`);
      
      // Reset form
      setTitle('');
      setDescription('');
      setLeadId('');
      setPartnerId('');
      setSmId('');
      setDueDate(format(new Date(), "yyyy-MM-dd"));
      setDueTime(format(new Date(), "HH:mm"));
      fileUpload.resetPending();
      setSharedWith([]);
      setShareWithAll(false);
      setShareOpen(false);
      setIsSaving(false);
      onOpenChange(false);
    } catch (error) {
      console.error('Error saving task:', error);
      toast.error('Failed to save reminder');
      setIsSaving(false);
    }
  };

  const handleToggleShareMember = (memberId: string) => {
    setSharedWith(prev => 
      prev.includes(memberId) 
        ? prev.filter(id => id !== memberId)
        : [...prev, memberId]
    );
    setShareWithAll(false);
  };

  const handleShareWithAll = () => {
    if (shareWithAll) {
      setSharedWith([]);
      setShareWithAll(false);
    } else {
      setSharedWith(availableUsers.map(m => m.userId || m.id));
      setShareWithAll(true);
    }
  };

  // Register/unregister with ModalContext for centralized back button handling
  const { registerModal, unregisterModal } = useModal();
  
  const imageViewerOpen = fileUpload.imageViewerState.isOpen;

  useEffect(() => {
    if (!open) return;

    const priority = imageViewerOpen ? 2 : 1;
    const handleClose = () => {
      if (imageViewerOpen) {
        fileUpload.closeImageViewer();
      } else {
        onOpenChange(false);
      }
    };

    registerModal('add-task-dialog', handleClose, priority);

    return () => {
      unregisterModal('add-task-dialog');
    };
  }, [open, imageViewerOpen, fileUpload, onOpenChange, registerModal, unregisterModal]);

  // Don't render if not open
  if (!open) return null;

  // Prevent closing dialog when image viewer is open
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen && fileUpload.imageViewerState.isOpen) {
      fileUpload.closeImageViewer();
      return;
    }
    onOpenChange(newOpen);
  };

  return (
    <>
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent side="bottom" className="h-[90vh] rounded-t-3xl flex flex-col">
        <SheetHeader className="pb-4 flex-shrink-0">
          <div className="flex items-center justify-center gap-2">
            <SheetTitle className="text-xl font-bold">Add Reminder</SheetTitle>
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={() => { setTitle(''); setDescription(''); setLeadId(''); setPartnerId(''); setSmId(''); setDueDate(format(new Date(), "yyyy-MM-dd")); setDueTime(format(new Date(), "HH:mm")); setSharedWith([]); setShareWithAll(false); setShareOpen(false); }}
              className="h-8 w-8 rounded-lg shrink-0"
              title="Clear form"
            >
              <RotateCcw className="h-4 w-4" />
            </Button>
          </div>
        </SheetHeader>
        
        <div className="flex-1 overflow-y-auto px-6">
        <div className="space-y-5 pb-8">
              
              {/* SECTION: What to remind */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 pb-1">
                  <Mic className="h-4 w-4 text-green-600" />
                  <h3 className="font-semibold text-green-800">What to remind?</h3>
                </div>
                <div className="space-y-2">
                  <div className="relative">
                    <Textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Enter reminder details or tap mic to speak..."
                      rows={4}
                      maxLength={500}
                      className="h-auto min-h-[100px] rounded-xl resize-none placeholder:text-muted-foreground/50 pr-12"
                    />
                    <Button
                      type="button"
                      variant={isListening ? "default" : "ghost"}
                      size="icon"
                      className={`absolute right-2 top-2 h-8 w-8 rounded-full ${isListening ? 'bg-red-500 hover:bg-red-600 animate-pulse' : ''}`}
                      onClick={toggleVoiceInput}
                      title={isListening ? "Stop listening" : "Start voice input"}
                    >
                      <Mic className={`h-4 w-4 ${isListening ? 'text-white' : ''}`} />
                    </Button>
                  </div>
                  <div className="flex justify-between items-center text-xs">
                    <span className="text-muted-foreground">
                      {isListening ? '🔴 Listening... tap mic to stop' : 'Tap mic for voice input • Max 500 characters'}
                    </span>
                    <span className={description.length > 450 ? "text-warning font-medium" : "text-muted-foreground"}>
                      {description.length}/500
                    </span>
                  </div>
                </div>
              </div>

              {/* SECTION: Due Date & Time */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 pb-1">
                  <Calendar className="h-4 w-4 text-green-600" />
                  <h3 className="font-semibold text-green-800">Due Date & Time</h3>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Date <span className="text-red-500">*</span></Label>
                    <Input
                      type="date"
                      value={dueDate}
                      onChange={(e) => setDueDate(e.target.value)}
                      className="h-12 placeholder:text-muted-foreground/50 [&::-webkit-calendar-picker-indicator]:ml-auto [&::-webkit-calendar-picker-indicator]:opacity-50 hover:[&::-webkit-calendar-picker-indicator]:opacity-100"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Time <span className="text-red-500">*</span></Label>
                    <Input
                      type="time"
                      value={dueTime}
                      onChange={(e) => setDueTime(e.target.value)}
                      className="h-12 placeholder:text-muted-foreground/50 [&::-webkit-calendar-picker-indicator]:ml-auto [&::-webkit-calendar-picker-indicator]:opacity-50 hover:[&::-webkit-calendar-picker-indicator]:opacity-100"
                    />
                  </div>
                </div>
              </div>

              {/* SECTION: Attachments */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 pb-1">
                  <h3 className="font-semibold text-green-800">Attachments</h3>
                </div>
                <FileUploadSection
                  files={fileUpload.pendingFiles}
                  onCameraCapture={fileUpload.handleCameraCapture}
                  onFileUpload={fileUpload.handleFileUpload}
                  onRemoveFile={fileUpload.handleRemovePendingFile}
                  onImageClick={fileUpload.openImageViewer}
                  imageViewerState={{
                    ...fileUpload.imageViewerState,
                    onClose: fileUpload.closeImageViewer,
                  }}
                  label=""
                  showLabel={false}
                  helpText={`Photos, documents, PDFs • Max ${config.maxFileUploads} files, ${config.maxFileUploadSizeMB}MB each`}
                  disabled={isSaving}
                />
              </div>

              {/* SECTION: Customer (Optional) */}
              {!hideCustomerSelection && !isBasic && !isRM && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 pb-1">
                    <User className="h-4 w-4 text-green-600" />
                    <h3 className="font-semibold text-green-800">Customer</h3>
                    <span className="text-xs text-muted-foreground">(Optional)</span>
                  </div>
                  <div className="space-y-2">
                    <Select value={leadId} onValueChange={setLeadId} disabled={leadsLoading}>
                      <SelectTrigger className="h-12">
                        <SelectValue placeholder={leadsLoading ? "Loading customers..." : "Select customer"} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No customer</SelectItem>
                        {leads.map(lead => (
                          <SelectItem key={lead.id} value={lead.id}>
                            {getLegacyCustomerName(lead)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {/* SECTION: Banker Contact (Optional) */}
              {!hideCustomerSelection && !isBasic && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 pb-1">
                    <CreditCard className="h-4 w-4 text-green-600" />
                    <h3 className="font-semibold text-green-800">Banker Contact</h3>
                    <span className="text-xs text-muted-foreground">(Optional)</span>
                  </div>
                  <div className="space-y-2">
                    <SearchableSelect
                      options={[
                        { value: "none", label: "No banker" },
                        ...[...bankers]
                          .sort((a, b) => {
                            const aFav = isBankerFavorite(a.id);
                            const bFav = isBankerFavorite(b.id);
                            if (aFav && !bFav) return -1;
                            if (!aFav && bFav) return 1;
                            return a.name.localeCompare(b.name);
                          })
                          .map(sm => ({
                            value: sm.id,
                            label: `${isBankerFavorite(sm.id) ? '★ ' : ''}${sm.name} - ${sm.bank}`,
                            displayLabel: sm.name
                          }))
                      ]}
                      value={smId}
                      onValueChange={setSmId}
                      placeholder={bankersLoading ? "Loading bankers..." : "Select banker"}
                      searchPlaceholder="Search bankers..."
                      emptyText="No banker found."
                    />
                  </div>
                </div>
              )}

              {/* SECTION: Partner (Optional) */}
              {!hideCustomerSelection && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 pb-1">
                    <Building className="h-4 w-4 text-green-600" />
                    <h3 className="font-semibold text-green-800">{usesDsaPartners ? "DSA Partners" : "Partner"}</h3>
                    <span className="text-xs text-muted-foreground">(Optional)</span>
                  </div>
                  <div className="space-y-2">
                    <SearchableSelect
                      options={[
                        { value: "none", label: "No partner" },
                        ...[...(usesDsaPartners ? dsaPartners : channelPartners)]
                          .sort((a, b) => {
                            if (a.isFavorite && !b.isFavorite) return -1;
                            if (!a.isFavorite && b.isFavorite) return 1;
                            return a.name.localeCompare(b.name);
                          })
                          .map(c => ({
                            value: c.id,
                            label: `${c.isFavorite ? '★ ' : ''}${c.name} (${c.type})`
                          }))
                      ]}
                      value={partnerId}
                      onValueChange={setPartnerId}
                      placeholder={(usesDsaPartners ? dsaPartnersLoading : partnersLoading) ? "Loading partners..." : "Select partner"}
                      searchPlaceholder="Search partners..."
                      emptyText="No partner found."
                    />
                  </div>
                </div>
              )}

              {/* SECTION: Share with colleagues */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 pb-1">
                  <Share2 className="h-4 w-4 text-green-600" />
                  <h3 className="font-semibold text-green-800">Share with Colleagues</h3>
                </div>
                <Collapsible open={shareOpen} onOpenChange={setShareOpen}>
                  <CollapsibleTrigger asChild>
                    <Button variant="outline" className="w-full justify-between h-12" type="button">
                      <span className="flex items-center gap-2">
                        <span>
                          {sharedWith.length > 0 
                            ? `Sharing with ${shareWithAll ? 'all' : sharedWith.length} colleague${sharedWith.length > 1 ? 's' : ''}`
                            : 'Tap to share'
                          }
                        </span>
                      </span>
                      <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${shareOpen ? 'rotate-180' : ''}`} />
                    </Button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="pt-3 space-y-2">
                    <div 
                      className="flex items-center gap-3 p-3 rounded-xl border cursor-pointer hover:bg-secondary/50 transition-colors"
                      onClick={handleShareWithAll}
                    >
                      <Checkbox checked={shareWithAll} onCheckedChange={handleShareWithAll} />
                      <div className="flex-1">
                        <span className="text-sm font-medium">Share with all</span>
                      </div>
                      <Users className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      {availableUsers.map((member) => {
                        const memberId = member.userId || member.id;
                        return (
                          <div 
                            key={memberId}
                            className="flex items-center gap-2 p-2.5 rounded-xl border cursor-pointer hover:bg-secondary/50 transition-colors"
                            onClick={() => handleToggleShareMember(memberId)}
                          >
                            <Checkbox 
                              checked={sharedWith.includes(memberId)} 
                              onCheckedChange={() => handleToggleShareMember(memberId)}
                            />
                            <span className="text-sm truncate">{member.name.split(' ')[0]}</span>
                          </div>
                        );
                      })}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              </div>
          </div>
        </div>

          {/* Submit Button */}
          <div className="flex-shrink-0 bg-background pt-4 pb-2 border-t">
          <Button
            onClick={handleSubmit}
            disabled={isSaving || fileUpload.isUploading}
            className="w-full h-14 text-base font-semibold rounded-xl bg-gradient-to-r from-[#2d5a6b] via-[#3a7680] to-[#4ea39f] hover:opacity-90 transition-all mt-6 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? (
              <>
                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                {fileUpload.pendingFiles.length > 0 ? 'Uploading & Saving...' : 'Saving...'}
              </>
            ) : (
              'Add Reminder'
            )}
          </Button>
          </div>
      </SheetContent>
    </Sheet>
    
  </>
  );
}
